import React, { useState, useEffect, useContext } from 'react';
import axiosInstance from '../../axiosConfig';
import { AuthContext } from '../../contexts/AuthContext';
import '../../styles/Referral.css';
// import { Circles } from 'react-loader-spinner';
import Notification from '../notification/Notification';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faMoneyBillWave } from '@fortawesome/free-solid-svg-icons';
import ResumeSkeleton from '../Loading/ResumeSkeleton';

const ReferFriend = () => {
    const [referralLink, setReferralLink] = useState('');
    const [paidReferralsCount, setPaidReferralsCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const { user } = useContext(AuthContext);
    const [notification, setNotification] = useState(null);
    const [bank, setBank] = useState('');
    const [accountName, setAccountName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [withdrawLoading, setWithdrawLoading] = useState(false);

    useEffect(() => {
        const fetchReferralData = async () => {
            try {
                const response = await axiosInstance.get('/api/auth/referral');
                setReferralLink(response.data.referralLink);
                setPaidReferralsCount(response.data.paidReferralsCount);
            } catch (error) {
                // console.error('Error fetching referral data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchReferralData();
    }, [user]);

    const handleCopyLink = () => {
        navigator.clipboard.writeText(referralLink).then(() => {
            setNotification({ message: 'Referral link copied successfully!', isError: false });
        }).catch(() => {
            setNotification({ message: 'Failed to copy referral link.', isError: true });
        });
    };

    const validateInputs = () => {
        const phoneRegex = /^\+?[1-9]\d{1,14}$/; // Basic international phone number validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!accountNumber.match(/^\d+$/)) {
            setNotification({ message: 'Account number must contain only digits.', isError: true });
            return false;
        }

        if (!phoneNumber.match(phoneRegex)) {
            setNotification({ message: 'Invalid phone number.', isError: true });
            return false;
        }

        if (!email.match(emailRegex)) {
            setNotification({ message: 'Invalid email address.', isError: true });
            return false;
        }

        if (!accountName) {
            setNotification({ message: 'Account name is required.', isError: true });
            return false;
        }

        return true;
    };

    const handleWithdrawRequest = async () => {
        if (!validateInputs()) {
            setNotification({ message: "Invalid inputs. Please check all fields for accuracy.", isError: true });
            return;
        }

        if (paidReferralsCount < 5) {
            setNotification({ message: 'You need at least 5 paid referrals to withdraw.', isError: true });
            return;
        }

        setWithdrawLoading(true);
        try {
            const response = await axiosInstance.put('/api/users/withdraw', {
                bank,
                accountName,
                accountNumber,
                phoneNumber,
                email,
            });

            setNotification({ message: response.data.message, isError: !response.data.success });
        } catch (error) {
            // console.error('Error during withdrawal request:', error);
            setNotification({ message: error.response?.data?.message || 'Failed to submit withdrawal request due to an unexpected error.', isError: true });
        } finally {
            setWithdrawLoading(false);
        }
    };

    return (
        <div className="refer-friend-container">
            {notification && (
                <Notification
                    message={notification.message}
                    isError={notification.isError}
                    onClose={() => setNotification(null)}
                />
            )}

            <h2>Refer a Friend</h2>
            <div className="card">
                <p>Share the link below to refer your friends and earn rewards:</p>
                <p>Note: You will receive a commission once your friends create an account and purchase their customized resume.</p>
                <p
                    style={{
                        fontWeight: 'bold',
                        fontSize: '1.5em',
                        color: '#007bff',
                        marginTop: '20px',
                    }}
                >Earn 25 Birr for each paid referral.</p>
            </div>

            {loading ? (
                <div className="loader">
                    <ResumeSkeleton/>
                </div>
            ) : (
                <div className="referral-info">
                    <input type="text" readOnly value={referralLink} />
                    <button onClick={handleCopyLink}>
                        <FontAwesomeIcon icon={faCopy} style={{ marginRight: '5px' }} />
                        Copy Link
                    </button>
                    <p className="earnings-label">You have {paidReferralsCount} paid referrals.</p>
                    <p style={{
                        fontWeight: 'bold',
                        fontSize: '1.5em',
                        color: '#007bff',
                        marginTop: '20px',
                    }}>
                        <FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: '10px' }} />
                        You have earned {paidReferralsCount * 25} Birr.
                    </p>
                    <div>
                        <p>Request a withdrawal when you have accumulated 5 or more paid referrals.</p>
                    </div>
                    <div className="withdraw-section">
                        <div className="form-group">
                            <label htmlFor="bank">Bank</label>
                            <select
                                id="bank"
                                value={bank}
                                onChange={(e) => setBank(e.target.value)}
                                disabled={paidReferralsCount < 5}
                                required
                            >
                                <option value="">Select Bank</option>
                                <option value="Nib International Bank">Nib International Bank</option>
                                <option value="Abay Bank">Abay Bank</option>
                                <option value="Addis International Bank">Addis International Bank</option>
                                <option value="Awash International Bank">Awash International Bank</option>
                                <option value="Bank of Abyssinia">Bank of Abyssinia</option>
                                <option value="Berhan International Bank">Berhan International Bank</option>
                                <option value="Bunna International Bank">Bunna International Bank</option>
                                <option value="Commercial Bank of Ethiopia">Commercial Bank of Ethiopia</option>
                                <option value="Cooperative Bank of Oromia">Cooperative Bank of Oromia</option>
                                <option value="Dashen Bank">Dashen Bank</option>
                                <option value="Enat Bank">Enat Bank</option>
                                <option value="Lion International Bank">Lion International Bank</option>
                                <option value="Oromia International Bank">Oromia International Bank</option>
                                <option value="Hibret Bank">Hibret Bank</option>
                                <option value="Wegagen Bank">Wegagen Bank</option>
                                <option value="Zemen Bank">Zemen Bank</option>
                                <option value="Hijra Bank">Hijra Bank</option>
                                <option value="Siinqee Bank">Siinqee Bank</option>
                                <option value="Tsedey Bank">Tsedey Bank</option>
                                <option value="Tsehay Bank">Tsehay Bank</option>
                                <option value="Gadaa Bank SC">Gadaa Bank SC</option>
                                <option value="Amhara Bank">Amhara Bank</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="accountName">Account Holder Name</label>
                            <input
                                id="accountName"
                                type="text"
                                value={accountName}
                                onChange={(e) => setAccountName(e.target.value)}
                                disabled={paidReferralsCount < 5}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="accountNumber">Bank Account Number</label>
                            <input
                                id="accountNumber"
                                type="text"
                                value={accountNumber}
                                onChange={(e) => setAccountNumber(e.target.value)}
                                disabled={paidReferralsCount < 5}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="phoneNumber">Phone Number</label>
                            <input
                                id="phoneNumber"
                                type="text"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                disabled={paidReferralsCount < 5}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={paidReferralsCount < 5}
                                required
                            />
                        </div>
                        <button
                            onClick={handleWithdrawRequest}
                            disabled={withdrawLoading || paidReferralsCount < 5}
                        >
                            {withdrawLoading ? 'Submitting...' : 'Submit Withdrawal Request'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReferFriend;
